import { supabase } from '../lib/supabase';

export const notificationService = {
  /**
   * Get user notification preferences
   */
  async getPreferences(userId) {
    try {
      const { data, error } = await supabase?.from('user_notification_preferences')?.select('*')?.eq('user_id', userId)?.single();

      if (error) {
        // If no preferences exist, return defaults
        if (error?.code === 'PGRST116') {
          return {
            newSongsEnabled: true,
            newLessonsEnabled: true,
            newContentEnabled: true,
            emailEnabled: true
          };
        }
        throw error;
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        newSongsEnabled: data?.new_songs_enabled,
        newLessonsEnabled: data?.new_lessons_enabled,
        newContentEnabled: data?.new_content_enabled,
        emailEnabled: data?.email_enabled,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  },

  /**
   * Update user notification preferences
   */
  async updatePreferences(userId, preferences) {
    try {
      const { data, error } = await supabase?.from('user_notification_preferences')?.upsert({
          user_id: userId,
          new_songs_enabled: preferences?.newSongsEnabled,
          new_lessons_enabled: preferences?.newLessonsEnabled,
          new_content_enabled: preferences?.newContentEnabled,
          email_enabled: preferences?.emailEnabled,
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_id'
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        newSongsEnabled: data?.new_songs_enabled,
        newLessonsEnabled: data?.new_lessons_enabled,
        newContentEnabled: data?.new_content_enabled,
        emailEnabled: data?.email_enabled,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  },

  /**
   * Get notification history for user
   */
  async getHistory(userId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('notification_history')?.select(`
          *,
          musicals (
            id,
            title,
            poster_url
          ),
          songs (
            id,
            title
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return (data || [])?.map(item => ({
        id: item?.id,
        userId: item?.user_id,
        notificationType: item?.notification_type,
        musicalId: item?.musical_id,
        musicalTitle: item?.musicals?.title,
        musicalPoster: item?.musicals?.poster_url,
        songId: item?.song_id,
        songTitle: item?.songs?.title,
        lessonId: item?.lesson_id,
        contentTitle: item?.content_title,
        contentDescription: item?.content_description,
        emailSentAt: item?.email_sent_at,
        emailStatus: item?.email_status,
        errorMessage: item?.error_message,
        createdAt: item?.created_at
      }));
    } catch (error) {
      console.error('Error fetching notification history:', error);
      throw error;
    }
  },

  /**
   * Get pending notifications count
   */
  async getPendingCount(userId) {
    try {
      const { count, error } = await supabase?.from('notification_queue')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('status', 'pending');

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error fetching pending notifications count:', error);
      throw error;
    }
  },

  /**
   * Process pending notifications (admin function)
   * This would typically be called by a scheduled job
   */
  async processPendingNotifications() {
    try {
      // Get pending notifications
      const { data: pendingNotifications, error: fetchError } = await supabase?.from('notification_queue')?.select(`
          *,
          user_profiles (
            email,
            full_name
          ),
          musicals (
            title,
            poster_url
          )
        `)?.eq('status', 'pending')?.lte('scheduled_for', new Date()?.toISOString())?.limit(50);

      if (fetchError) throw fetchError;

      if (!pendingNotifications || pendingNotifications?.length === 0) {
        return { success: true, processed: 0 };
      }

      // Prepare notifications for edge function
      const notifications = pendingNotifications?.map(notif => ({
        id: notif?.id,
        user_email: notif?.user_profiles?.email,
        user_name: notif?.user_profiles?.full_name,
        content_title: notif?.content_title,
        content_description: notif?.content_description,
        musical_title: notif?.musicals?.title,
        app_url: `${window.location?.origin}/musical-progress-page?id=${notif?.musical_id}`
      }));

      // Call edge function to send emails
      const { data: edgeFunctionData, error: edgeFunctionError } = await supabase?.functions?.invoke(
        'send-content-notifications',
        {
          body: { notifications }
        }
      );

      if (edgeFunctionError) throw edgeFunctionError;

      // Update notification queue and create history records
      for (const result of edgeFunctionData?.results) {
        const notification = pendingNotifications?.find(n => n?.id === result?.notification_id);
        
        if (result?.success) {
          // Move to history
          await supabase?.from('notification_history')?.insert({
              user_id: notification?.user_id,
              notification_type: notification?.notification_type,
              musical_id: notification?.musical_id,
              song_id: notification?.song_id,
              lesson_id: notification?.lesson_id,
              content_title: notification?.content_title,
              content_description: notification?.content_description,
              email_status: 'sent',
              email_sent_at: new Date()?.toISOString()
            });

          // Remove from queue
          await supabase?.from('notification_queue')?.delete()?.eq('id', result?.notification_id);
        } else {
          // Update queue with error
          await supabase?.from('notification_queue')?.update({
              status: 'failed',
              error_message: result?.error
            })?.eq('id', result?.notification_id);
        }
      }

      return {
        success: true,
        processed: edgeFunctionData?.total_sent,
        failed: edgeFunctionData?.total_failed
      };
    } catch (error) {
      console.error('Error processing pending notifications:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive analytics for notification delivery and engagement
   * @param {Object} filters - Optional filters for date range and content type
   * @returns {Promise<Object>} Analytics data including delivery rates, engagement metrics, and content performance
   */
  async getNotificationAnalytics(filters = {}) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      // Get user profile to verify SuperUser role
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();

      if (profileError || !profile || profile?.role !== 'SuperUser') {
        throw new Error('Unauthorized: SuperUser access required');
      }

      const { startDate, endDate, contentType } = filters;

      // Build base query
      let query = supabase?.from('content_notifications')?.select(`
          id,
          user_id,
          content_type,
          content_id,
          notification_type,
          status,
          delivery_status,
          opened_at,
          clicked_at,
          created_at,
          sent_at,
          error_message
        `);

      // Apply date filters
      if (startDate) {
        query = query?.gte('created_at', startDate);
      }
      if (endDate) {
        query = query?.lte('created_at', endDate);
      }
      if (contentType) {
        query = query?.eq('content_type', contentType);
      }

      const { data: notifications, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate analytics metrics
      const totalNotifications = notifications?.length || 0;
      const deliveredNotifications = notifications?.filter(n => n?.delivery_status === 'delivered')?.length || 0;
      const failedNotifications = notifications?.filter(n => n?.delivery_status === 'failed')?.length || 0;
      const bouncedNotifications = notifications?.filter(n => n?.delivery_status === 'bounced')?.length || 0;
      const openedNotifications = notifications?.filter(n => n?.opened_at)?.length || 0;
      const clickedNotifications = notifications?.filter(n => n?.clicked_at)?.length || 0;

      // Calculate rates
      const deliveryRate = totalNotifications > 0 ? ((deliveredNotifications / totalNotifications) * 100)?.toFixed(2) : 0;
      const openRate = deliveredNotifications > 0 ? ((openedNotifications / deliveredNotifications) * 100)?.toFixed(2) : 0;
      const clickThroughRate = openedNotifications > 0 ? ((clickedNotifications / openedNotifications) * 100)?.toFixed(2) : 0;
      const failureRate = totalNotifications > 0 ? ((failedNotifications / totalNotifications) * 100)?.toFixed(2) : 0;
      const bounceRate = totalNotifications > 0 ? ((bouncedNotifications / totalNotifications) * 100)?.toFixed(2) : 0;

      // Content type performance breakdown
      const contentPerformance = {};
      notifications?.forEach(notification => {
        const type = notification?.content_type || 'unknown';
        if (!contentPerformance?.[type]) {
          contentPerformance[type] = {
            total: 0,
            delivered: 0,
            opened: 0,
            clicked: 0,
            failed: 0
          };
        }
        contentPerformance[type].total++;
        if (notification?.delivery_status === 'delivered') contentPerformance[type].delivered++;
        if (notification?.opened_at) contentPerformance[type].opened++;
        if (notification?.clicked_at) contentPerformance[type].clicked++;
        if (notification?.delivery_status === 'failed') contentPerformance[type].failed++;
      });

      // Calculate content type metrics
      const contentMetrics = Object.keys(contentPerformance)?.map(type => {
        const perf = contentPerformance?.[type];
        return {
          contentType: type,
          total: perf?.total,
          deliveryRate: perf?.total > 0 ? ((perf?.delivered / perf?.total) * 100)?.toFixed(2) : 0,
          openRate: perf?.delivered > 0 ? ((perf?.opened / perf?.delivered) * 100)?.toFixed(2) : 0,
          clickRate: perf?.opened > 0 ? ((perf?.clicked / perf?.opened) * 100)?.toFixed(2) : 0,
          failureRate: perf?.total > 0 ? ((perf?.failed / perf?.total) * 100)?.toFixed(2) : 0
        };
      });

      // User behavior insights - notification preferences
      const { data: preferences, error: prefError } = await supabase?.from('notification_preferences')?.select('*');

      const totalUsers = preferences?.length || 0;
      const emailEnabled = preferences?.filter(p => p?.email_notifications)?.length || 0;
      const optOutRate = totalUsers > 0 ? (((totalUsers - emailEnabled) / totalUsers) * 100)?.toFixed(2) : 0;

      return {
        success: true,
        analytics: {
          overview: {
            totalNotifications,
            deliveredNotifications,
            failedNotifications,
            bouncedNotifications,
            openedNotifications,
            clickedNotifications,
            deliveryRate: parseFloat(deliveryRate),
            openRate: parseFloat(openRate),
            clickThroughRate: parseFloat(clickThroughRate),
            failureRate: parseFloat(failureRate),
            bounceRate: parseFloat(bounceRate)
          },
          contentPerformance: contentMetrics,
          userBehavior: {
            totalUsers,
            emailEnabled,
            emailDisabled: totalUsers - emailEnabled,
            optOutRate: parseFloat(optOutRate)
          },
          recentNotifications: notifications?.slice(0, 10) || []
        }
      };
    } catch (error) {
      console.error('Error fetching notification analytics:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  },

  /**
   * Get notification trends over time
   * @param {string} period - Time period: 'daily', 'weekly', 'monthly'
   * @param {number} limit - Number of periods to fetch
   * @returns {Promise<Object>} Trend data for charts
   */
  async getNotificationTrends(period = 'daily', limit = 30) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      // Verify SuperUser role
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();

      if (profileError || !profile || profile?.role !== 'SuperUser') {
        throw new Error('Unauthorized: SuperUser access required');
      }

      // Calculate date range based on period
      const endDate = new Date();
      const startDate = new Date();
      
      if (period === 'daily') {
        startDate?.setDate(startDate?.getDate() - limit);
      } else if (period === 'weekly') {
        startDate?.setDate(startDate?.getDate() - (limit * 7));
      } else if (period === 'monthly') {
        startDate?.setMonth(startDate?.getMonth() - limit);
      }

      const { data: notifications, error } = await supabase?.from('content_notifications')?.select('created_at, delivery_status, opened_at, clicked_at')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by period
      const trends = {};
      notifications?.forEach(notification => {
        const date = new Date(notification.created_at);
        let key;
        
        if (period === 'daily') {
          key = date?.toISOString()?.split('T')?.[0];
        } else if (period === 'weekly') {
          const weekStart = new Date(date);
          weekStart?.setDate(date?.getDate() - date?.getDay());
          key = weekStart?.toISOString()?.split('T')?.[0];
        } else if (period === 'monthly') {
          key = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
        }

        if (!trends?.[key]) {
          trends[key] = {
            date: key,
            sent: 0,
            delivered: 0,
            opened: 0,
            clicked: 0,
            failed: 0
          };
        }

        trends[key].sent++;
        if (notification?.delivery_status === 'delivered') trends[key].delivered++;
        if (notification?.delivery_status === 'failed') trends[key].failed++;
        if (notification?.opened_at) trends[key].opened++;
        if (notification?.clicked_at) trends[key].clicked++;
      });

      return {
        success: true,
        trends: Object.values(trends)
      };
    } catch (error) {
      console.error('Error fetching notification trends:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }
};

export default notificationService;
function getNotificationAnalytics(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getNotificationAnalytics is not implemented yet.', args);
  return null;
}

export { getNotificationAnalytics };
function getNotificationTrends(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getNotificationTrends is not implemented yet.', args);
  return null;
}

export { getNotificationTrends };